import { useEffect, useState } from 'react'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { APP_DATA_REFRESH_EVENT, requestAppDataRefresh } from '../utils/appDataRefresh'

let sourceRefCount = 0
let removeBrowserSources: (() => void) | null = null
let nativeHandles: PluginListenerHandle[] = []
let nativeSetupCancelled = false

const installRefreshSources = (): (() => void) => {
  sourceRefCount += 1
  if (sourceRefCount > 1) {
    return () => {
      sourceRefCount = Math.max(0, sourceRefCount - 1)
    }
  }

  nativeSetupCancelled = false

  const onVisible = () => {
    if (document.visibilityState === 'visible') requestAppDataRefresh('visibility')
  }
  const onFocus = () => requestAppDataRefresh('focus')
  window.addEventListener('focus', onFocus)
  document.addEventListener('visibilitychange', onVisible)
  removeBrowserSources = () => {
    window.removeEventListener('focus', onFocus)
    document.removeEventListener('visibilitychange', onVisible)
  }

  if (Capacitor.isNativePlatform()) {
    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) requestAppDataRefresh('native-app-active')
    }).then((handle) => {
      if (nativeSetupCancelled) void handle.remove()
      else nativeHandles.push(handle)
    }).catch(() => undefined)

    void CapacitorApp.addListener('resume', () => {
      requestAppDataRefresh('native-resume')
    }).then((handle) => {
      if (nativeSetupCancelled) void handle.remove()
      else nativeHandles.push(handle)
    }).catch(() => undefined)
  }

  return () => {
    sourceRefCount = Math.max(0, sourceRefCount - 1)
    if (sourceRefCount > 0) return
    nativeSetupCancelled = true
    removeBrowserSources?.()
    removeBrowserSources = null
    nativeHandles.forEach((handle) => void handle.remove())
    nativeHandles = []
  }
}

export const useAppDataRefreshSignal = (): number => {
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    const releaseSources = installRefreshSources()
    const onRefresh = () => setRefreshToken((value) => value + 1)
    window.addEventListener(APP_DATA_REFRESH_EVENT, onRefresh)
    return () => {
      window.removeEventListener(APP_DATA_REFRESH_EVENT, onRefresh)
      releaseSources()
    }
  }, [])

  return refreshToken
}
